// interface กลางของ AI ที่รันในเบราว์เซอร์ผู้ใช้ — ฝั่งอื่นเรียกผ่านไฟล์นี้เท่านั้น (ห้ามเรียก web-llm ตรง)
//  generate({system, messages, maxTokens, temperature, onToken, json}) -> {ok, text, mock?, code?, message?}  (ไม่ throw)
import { IS_MOCK } from "../config";
import { createMockBackend } from "./mockBackend";
import { getSelectedLlm, setLlmConsent } from "./models";

export const UNSUPPORTED_MSG = "เบราว์เซอร์นี้ยังไม่รองรับ WebGPU จึงรัน AI ในเครื่องไม่ได้ — ลองใช้ Chrome หรือ Edge เวอร์ชันล่าสุดบนคอมพิวเตอร์ หรือมือถือรุ่นใหม่";
export const NOT_READY_MSG = "ยังไม่ได้โหลดโมเดล AI — กดโหลดโมเดลก่อน (ดาวน์โหลดครั้งเดียว ครั้งต่อไปโหลดจากแคช)";

const initial = () => ({ status: "idle", modelId: "", progress: 0, text: "", error: "" }); // idle|loading|ready|error
let state = initial();
let backend = null;
let current = null; // งานที่กำลังสร้างคำตอบ {cancelled}
const subs = new Set();
const set = (patch) => {
  state = { ...state, ...patch };
  subs.forEach((f) => f(state));
};

const makeBackend = async () => {
  if (backend) return backend;
  if (IS_MOCK) return setBackend(createMockBackend());
  backend = (await import("./webllmBackend")).createWebLlmBackend();
  return backend;
};

/** เปลี่ยน backend (ใช้ใน test หรือโหมดจำลอง) */
export function setBackend(b) {
  backend = b;
  current = null;
  state = b && b.alwaysReady ? { status: "ready", modelId: "mock", progress: 1, text: "", error: "" } : initial();
  subs.forEach((f) => f(state));
  return backend;
}
export const resetEngine = () => setBackend(null);

export const getState = () => state;
export const subscribe = (fn) => {
  subs.add(fn);
  return () => subs.delete(fn);
};
export const isMockEngine = () => IS_MOCK || !!(backend && backend.mock);
export const isSupported = () => (backend ? backend.supported() : IS_MOCK || (typeof navigator !== "undefined" && !!navigator.gpu));
export const isReady = () => state.status === "ready";

/** โหลดโมเดล ; onProgress(0..1, text) ; คืน {ok, message} ไม่ throw */
export async function load(modelId = getSelectedLlm()) {
  if (!isSupported()) {
    set({ status: "error", error: UNSUPPORTED_MSG });
    return { ok: false, code: "ai_unsupported", message: UNSUPPORTED_MSG };
  }
  if (state.status === "loading") return { ok: false, code: "ai_loading", message: "กำลังโหลดโมเดลอยู่" };
  if (state.status === "ready" && state.modelId === modelId) return { ok: true };
  set({ status: "loading", modelId, progress: 0, text: "เริ่มโหลด...", error: "" });
  try {
    const b = await makeBackend();
    await b.load(modelId, (p, text) => set({ progress: Math.max(0, Math.min(1, p || 0)), text: text || "" }));
    setLlmConsent(modelId, true);
    set({ status: "ready", modelId, progress: 1, text: "", error: "" });
    return { ok: true };
  } catch (e) {
    const message = "โหลดโมเดลไม่สำเร็จ: " + ((e && e.message) || "ไม่ทราบสาเหตุ") + " (ลองใหม่ หรือเลือกโมเดลที่เล็กกว่า)";
    set({ status: "error", error: message, progress: 0 });
    return { ok: false, code: "ai_load_failed", message };
  }
}

/** โหลดอัตโนมัติเฉพาะที่เคยยอมรับ + ยังอยู่ในแคช (ไม่ดาวน์โหลดใหม่เงียบ ๆ) */
export async function loadIfCached(modelId = getSelectedLlm()) {
  if (state.status !== "idle" || !isSupported()) return false;
  try {
    const b = await makeBackend();
    if (!(await b.isCached(modelId))) return false;
  } catch (e) {
    return false;
  }
  return (await load(modelId)).ok;
}

/** โหลดอัตโนมัติทุกหน้า (ผู้ใช้สั่ง: ไม่ต้องถามก่อนดาวน์โหลด) — ไม่รองรับ WebGPU/โหมด mock = ข้าม ; มีในแคชก็โหลดจากแคช */
export async function autoLoad(modelId = getSelectedLlm()) {
  if (IS_MOCK || !isSupported() || state.status !== "idle") return false;
  return (await load(modelId)).ok;
}

export async function unload() {
  if (backend) await backend.unload();
  current = null;
  state = backend && backend.alwaysReady ? state : initial();
  subs.forEach((f) => f(state));
}

/** ยกเลิกคำตอบที่กำลังสร้าง -> generate คืน ai_cancelled */
export function abort() {
  if (!current) return;
  current.cancelled = true;
  if (backend) backend.interrupt();
}

let chain = Promise.resolve();
/**
 * สร้างคำตอบ — เรียกซ้อนกันจะเข้าคิว ; background:true = งานเบื้องหลัง (เช่น สรุปความจำ) ถูกยกเลิกทันทีเมื่อมีงานของผู้ใช้เข้ามา
 */
export function generate(opts = {}) {
  if (!opts.background && current && current.background) abort();
  const p = chain.then(() => run(opts));
  chain = p.catch(() => {});
  return p;
}

async function run({ system, messages, maxTokens = 400, temperature = 0.8, onToken, json = false, background = false } = {}) {
  if (!backend && IS_MOCK) await makeBackend();
  if (!isSupported()) return { ok: false, code: "ai_unsupported", message: UNSUPPORTED_MSG };
  if (state.status !== "ready" || !backend) return { ok: false, code: "ai_not_ready", message: NOT_READY_MSG };
  const job = { cancelled: false, background };
  current = job;
  const all = [];
  if (system) all.push({ role: "system", content: system });
  (messages || []).forEach((m) => all.push({ role: m.role === "assistant" ? "assistant" : "user", content: String(m.content || "") }));
  let text = "";
  try {
    for await (const tok of backend.stream({ messages: all, maxTokens, temperature, json })) {
      if (job.cancelled) break;
      text += tok;
      if (onToken) {
        try {
          onToken(tok, text);
        } catch (e) {
          /* ผู้ฟังพังไม่ควรทำให้คำตอบล้ม */
        }
      }
    }
  } catch (e) {
    return { ok: false, code: job.cancelled ? "ai_cancelled" : "ai_error", message: job.cancelled ? "ยกเลิกการสร้างคำตอบแล้ว" : "AI ขัดข้อง ลองใหม่อีกครั้ง" };
  } finally {
    if (current === job) current = null;
  }
  if (job.cancelled) return { ok: false, code: "ai_cancelled", message: "ยกเลิกการสร้างคำตอบแล้ว" };
  text = text.trim();
  if (!text) return { ok: false, code: "ai_error", message: "AI ไม่ได้ตอบกลับ ลองใหม่อีกครั้ง" };
  return { ok: true, text, ...(backend.mock ? { mock: true } : {}) };
}
